import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useCallback, useEffect, useState } from "react";
import { clsx } from "clsx";
import { Collapse } from "@crehana/compass.ui";
import { Clamp } from "@crehana/ui";
import useNewDashboardContext from "../context/useNewDashboardContext";
import MenuCollapsableLink from "./MenuCollapsableLink";
import MenuDesktopLinkInner from "./MenuDesktopLink";
import SubMenuDesktopLink from "./SubMenuDesktopLink";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var MenuItem = function MenuItem(_ref) {
  var _route$decorations, _route$decorations2, _MenuCollapsableLink;
  var route = _ref.route,
    isDark = _ref.isDark,
    dataTestId = _ref.dataTestId,
    isBottomLink = _ref.isBottomLink,
    _ref$isSidebarV2Enabl = _ref.isSidebarV2Enabled,
    isSidebarV2Enabled = _ref$isSidebarV2Enabl === void 0 ? false : _ref$isSidebarV2Enabl;
  var _useNewDashboardConte = useNewDashboardContext(),
    sidebarCollapsed = _useNewDashboardConte.sidebarCollapsed,
    currentRoute = _useNewDashboardConte.currentRoute,
    onRouteChange = _useNewDashboardConte.onRouteChange;
  var _ref2 = route,
    _ref2$nested = _ref2.nested,
    nested = _ref2$nested === void 0 ? [] : _ref2$nested,
    _ref2$defaultOpen = _ref2.defaultOpen,
    defaultOpen = _ref2$defaultOpen === void 0 ? false : _ref2$defaultOpen,
    _ref2$startExpanded = _ref2.startExpanded,
    startExpanded = _ref2$startExpanded === void 0 ? false : _ref2$startExpanded,
    _ref2$closedOnCollaps = _ref2.closedOnCollapse,
    closedOnCollapse = _ref2$closedOnCollaps === void 0 ? false : _ref2$closedOnCollaps,
    newTag = _ref2.newTag,
    RightIcon = _ref2.rightIcon;
  var isDefaultExpanded = sidebarCollapsed ? false : startExpanded;
  var _useState = useState(isDefaultExpanded || false),
    _useState2 = _slicedToArray(_useState, 2),
    isSubmenuExpanded = _useState2[0],
    setIsSubmenuExpanded = _useState2[1];
  var isSelectedSingleRoute = (currentRoute === null || currentRoute === void 0 ? void 0 : currentRoute.key) === route.key;
  var hasNestedRoutes = nested.length > 0;
  var hasSelectedNestedRoute = nested.some(function (n) {
    return n.key === (currentRoute === null || currentRoute === void 0 ? void 0 : currentRoute.key);
  });
  var isSelected = hasSelectedNestedRoute || isSelectedSingleRoute;
  var ActiveIcon = route.activeIcon || route.icon;
  var shouldHaveSelectedStyles = hasSelectedNestedRoute || isSelectedSingleRoute || isSubmenuExpanded;

  // Decorations
  var lineTop = !!((_route$decorations = route.decorations) !== null && _route$decorations !== void 0 && _route$decorations.lineTop);
  var subTitle = (_route$decorations2 = route.decorations) === null || _route$decorations2 === void 0 ? void 0 : _route$decorations2.subTitle;
  useEffect(function () {
    if (defaultOpen) {
      var timeout = setTimeout(function () {
        setIsSubmenuExpanded(true);
      }, 120);
      return function () {
        return clearTimeout(timeout);
      };
    }
  }, [defaultOpen]);
  useEffect(function () {
    if (sidebarCollapsed && closedOnCollapse) return;
    setIsSubmenuExpanded(isSelected);
  }, [isSelected]);
  useEffect(function () {
    if (!hasSelectedNestedRoute) setIsSubmenuExpanded(false);
  }, [hasSelectedNestedRoute]);
  useEffect(function () {
    if (hasNestedRoutes) {
      if (sidebarCollapsed && isSubmenuExpanded) {
        // Close all submenus when sidebar is collapsed
        setIsSubmenuExpanded(false);
      } else if (!sidebarCollapsed && !isSubmenuExpanded && hasSelectedNestedRoute) {
        // Open submenu when sidebar is expanded and submenu is selected
        setIsSubmenuExpanded(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNestedRoutes, sidebarCollapsed]);
  var onClickItem = useCallback(function () {
    onRouteChange(route);
  }, [onRouteChange, route]);
  var onChangeExpansionState = useCallback(function () {
    setIsSubmenuExpanded(function (prev) {
      return !prev;
    });
  }, [setIsSubmenuExpanded]);
  var renderContent = useCallback(function () {
    if (sidebarCollapsed) {
      return _MenuCollapsableLink || (_MenuCollapsableLink = /*#__PURE__*/_jsx(MenuCollapsableLink, {
        isDark: isDark,
        route: route,
        isOpened: isSubmenuExpanded,
        subRoutes: nested,
        currentRoute: currentRoute,
        isSelected: shouldHaveSelectedStyles,
        setIsOpenEnd: setIsSubmenuExpanded,
        onSelectNestedRoute: onRouteChange,
        isSidebarV2Enabled: isSidebarV2Enabled
      }));
    }
    var commonIconClassnames = clsx('fill-current tw-w-2.4 tw-min-w-2.4 tw-max-h-2.4', shouldHaveSelectedStyles ? 'tw-text-primary-light-400 dark:text-success-200' : 'tw-text-neutral-light-700 dark:tw-text-neutral-dark-300');
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(MenuDesktopLinkInner, {
        isSelected: shouldHaveSelectedStyles,
        isDark: isDark,
        tourSelector: route.tourSelector,
        expandable: hasNestedRoutes,
        dataTestId: dataTestId,
        onClickItem: onChangeExpansionState,
        rightIcon: RightIcon,
        isBottomLink: isBottomLink,
        isSidebarV2Enabled: isSidebarV2Enabled,
        children: [shouldHaveSelectedStyles ? /*#__PURE__*/_jsx(ActiveIcon, {
          size: isSidebarV2Enabled ? 16 : 20,
          className: commonIconClassnames,
          ignoreColor: true
        }) : /*#__PURE__*/_jsx(route.icon, {
          size: isSidebarV2Enabled ? 16 : 20,
          className: commonIconClassnames,
          ignoreColor: true
        }), /*#__PURE__*/_jsx(Clamp, {
          clamp: 1,
          title: route.label.length > 20 ? route.label : undefined,
          className: clsx('ml-8 break-all', {
            'tw-font-subtitle5 ': !isBottomLink,
            'tw-font-caption': isBottomLink,
            'text-base-main': shouldHaveSelectedStyles && !isDark,
            'tw-text-neutral-light-700': !shouldHaveSelectedStyles && !isDark,
            'text-white': shouldHaveSelectedStyles && isDark,
            'tw-text-neutral-dark-300': !shouldHaveSelectedStyles && isDark
          }),
          children: route.label
        }), Boolean(newTag) && /*#__PURE__*/_jsx("span", {
          className: clsx('font-caption ml-8', isDark ? 'text-secondary-main' : 'text-primary-light'),
          children: newTag
        })]
      }, route.key), hasNestedRoutes && /*#__PURE__*/_jsx(Collapse, {
        open: isSubmenuExpanded,
        animated: !startExpanded,
        "data-testid": "menu-item-collapse",
        children: /*#__PURE__*/_jsx("ul", {
          className: "block w-full pt-4",
          "data-testid": "menu-item-collapse-ul",
          children: nested.map(function (subRoute) {
            return /*#__PURE__*/_jsx(SubMenuDesktopLink, {
              route: subRoute,
              isDark: isDark,
              isSelectedURL: subRoute.key === (currentRoute === null || currentRoute === void 0 ? void 0 : currentRoute.key),
              onClick: function onClick() {
                return onRouteChange(subRoute);
              },
              isSidebarV2Enabled: isSidebarV2Enabled
            }, subRoute.key);
          })
        })
      })]
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoute, hasNestedRoutes, isDark, isSubmenuExpanded, nested, onRouteChange, route, shouldHaveSelectedStyles, sidebarCollapsed]);
  var renderLineTop = lineTop && /*#__PURE__*/_jsx("div", {
    className: clsx('w-full border-t border-solid my-4', isDark ? 'border-base-lighter-16' : 'border-gray-light')
  });
  var renderSubTitle = subTitle && /*#__PURE__*/_jsx("div", {
    className: clsx('font-label my-16 mx-12 sm:ml-24 sm:mr-16 sm:min-w-212', isDark ? 'text-base-lighter' : 'text-base-main', sidebarCollapsed && 'hidden'),
    children: subTitle
  });
  if (hasNestedRoutes) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [renderLineTop, renderSubTitle, renderContent()]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [renderLineTop, renderSubTitle, /*#__PURE__*/_jsx("div", {
      className: "w-full",
      onClick: onClickItem,
      onKeyDown: onClickItem,
      children: renderContent()
    })]
  });
};
export default MenuItem;